<template>
    <div class="chart-configuration">
        <div class="card">
            <div class="card-body">
                <div class="row">
                    <div :class="{ 'col-4': render_large_chart }">
                        <div class="alert alert-warning mt-2" v-if="chart.id">
                            Warning: Changing the chart will affect all reports that use this chart.
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Chart Type</label>
                            <select class="form-select" id="chart-type-select" v-model="local_chart.type"
                                :class="{ 'is-invalid': local_errors.chart_type !== '' }"
                                @change="changeChartTypeSelection" aria-label="Chart Type">
                                <option value="" disabled selected>Select the chart type...</option>
                                <option value="100_percent_stacked_column">100% Stacked Column Chart</option>
                                <option value="100_percent_stacked_bar">100% Stacked Horizontal Bar Chart</option>
                                <option value="bar_line">Bar-Line Combo Chart</option>
                                <option value="column">Column Chart</option>
                                <option value="cumulative_line">Cumulative Line Chart</option>
                                <option value="horizontal_bar">Horizontal Bar Chart</option>
                                <option value="information_card">Information Card</option>
                                <option value="line">Line Chart</option>
                                <option value="multi_dimension_table">Multi-Dimension Table</option>
                                <option value="multi_metric_scorecard">Multi-Metric Scorecard</option>
                                <option value="pie">Pie Chart</option>  
                                <option value="scorecard">Scorecard</option>
                                <option value="stacked_bar">Stacked Horizontal Bar Chart</option>
                                <option value="stacked_column">Stacked Column Chart</option>
                                <option value="table">Table</option>   
                            </select>
                            <div class="text-danger invalid-feedback" v-if="local_errors.chart_type !== ''">
                                {{ local_errors.chart_type }}
                            </div>
                        </div>
                        <div class="mb-3" v-if="local_chart.type !== 'information_card'">
                            <div class="row">
                                <div class="col">
                                    <label class="form-label">Dimensions</label>
                                    <button type="button" id="add-new-dimension" class="btn btn-xs btn-secondary float-end"
                                        data-bs-toggle="modal" data-bs-target="#add-metric-filter-modal"
                                        @click="add_new_option = 'Dimension'">
                                        <i class="fas fa-plus" data-bs-toggle="tooltip"
                                            title="Creating a new Dimension option will add it to the Dimension option dropdown."></i> Add New
                                    </button>
                                </div>
                            </div>
                            <accordion-multi-select id="dimensions-multi-select" 
                                :options="dimension_options"  
                                :show_hidden_fields="true"  
                                :max_num_values="max_values.dimensions" 
                                @input="local_errors.group_by = ''"
                                v-model="local_chart.group_by" 
                                @update:modelValue="changeSelection('group_by')"
                                :field_name="'dimensions'" 
                                :class="{ 'is-invalid': local_errors.group_by !== '' }">
                            </accordion-multi-select>
                            <div class="text-danger invalid-feedback" v-if="local_errors.group_by !== ''">
                                {{ local_errors.group_by }}
                            </div>
                            <div v-if="local_chart.group_by && local_chart.group_by.length > 0">
                                <template v-for="(dimension, index) in local_chart.group_by" :key="index">
                                    <a v-if="!isColumnType(dimension)"
                                       href="#" 
                                       class="me-2"
                                       style="font-size: 0.75rem;"
                                       data-bs-toggle="modal" 
                                       data-bs-target="#add-metric-filter-modal"
                                       @click.prevent="editMetricFilter(dimension, 'Dimension')">
                                        {{ dimension.admin_name || dimension.text }} <i class="fas fa-external-link-alt"></i>
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div :class="{ 'col-4': render_large_chart }" v-if="local_chart.type !== 'information_card'">
                        <div class="mb-3">
                            <div class="row">
                                <div class="col">
                                    <label class="form-label">Metrics</label>
                                    <button type="button" id="add-new-metric" class="btn btn-xs btn-secondary float-end"
                                        data-bs-toggle="modal" data-bs-target="#add-metric-filter-modal"
                                        @click="add_new_option = 'Metric'">
                                        <i class="fas fa-plus" data-bs-toggle="tooltip"
                                            title="Creating a new Metric option will add it to the Metrics option dropdown."></i> Add New
                                    </button>
                                </div>
                            </div>
                            <accordion-multi-select id="metrics-multi-select" 
                                :options="metric_options" 
                                @input="local_errors.metrics = ''" 
                                v-model="local_chart.metrics" 
                                @update:modelValue="changeSelection('metrics')"
                                :field_name="'metrics'"
                                :show_hidden_fields="true" 
                                :class="{ 'is-invalid': local_errors.metrics !== '' }"
                                :max_num_values="max_metrics">
                            </accordion-multi-select>
                            <div class="text-danger invalid-feedback" v-if="local_errors.metrics !== ''">
                                {{ local_errors.metrics }}
                            </div>
                            <div v-if="local_chart.metrics && local_chart.metrics.length > 0">
                                <template v-for="(metric, index) in local_chart.metrics" :key="index">
                                    <a v-if="!isColumnType(metric)"
                                       href="#" 
                                       class="me-2"
                                       style="font-size: 0.75rem;"
                                       data-bs-toggle="modal" 
                                       data-bs-target="#add-metric-filter-modal"
                                       @click.prevent="editMetricFilter(metric, 'Metric')">
                                        {{ metric.admin_name || metric.text }} <i class="fas fa-external-link-alt"></i>
                                    </a>
                                </template>
                            </div>
                        </div>
                        <div class="mb-3">
                            <div class="row">
                                <div class="col">
                                    <label class="form-label">Filters (Optional)</label>
                                    <button type="button" id="add-new-filter" class="btn btn-xs btn-secondary float-end"
                                        data-bs-toggle="modal" data-bs-target="#add-metric-filter-modal"
                                        @click="add_new_option = 'Filter'">
                                        <span>
                                            <i class="fas fa-plus" data-bs-toggle="tooltip"
                                                title="Creating a new Filter option will add it to the Filters option dropdown."></i>
                                        </span> Add New
                                    </button>
                                </div>
                            </div>
                            <accordion-multi-select id="filters-multi-select" 
                                :options="filter_options"
                                v-model="local_chart.filter" 
                                @update:modelValue="changeSelection('filter')"
                                :field_name="'filters'" 
                                :show_hidden_fields="true">
                            </accordion-multi-select>
                            <div v-if="local_chart.filter && local_chart.filter.length > 0">
                                <template v-for="(filter, index) in local_chart.filter" :key="index">
                                    <a v-if="!isColumnType(filter)"
                                       href="#" 
                                       class="me-2"
                                       style="font-size: 0.75rem;"
                                       data-bs-toggle="modal" 
                                       data-bs-target="#add-metric-filter-modal"
                                       @click.prevent="editMetricFilter(filter, 'Filter')">
                                        {{ filter.admin_name || filter.text }} <i class="fas fa-external-link-alt"></i>
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div :class="{ 'col-4': render_large_chart }" v-if="local_chart.type !== 'information_card'">
                        <div class="mb-3">
                            <label class="form-label" v-if="local_chart.type !== 'scorecard' && local_chart.type !== 'multi_metric_scorecard'">Order By</label>
                            <label class="form-label" v-else>Order By (Top Value is Shown)</label>
                            <button type="button" class="btn btn-xs" @click="addOrderBy" v-if="max_values.order_by > order_bys.length">
                                <span><i class="fas fa-plus"></i></span>
                            </button>
                            <div class="input-group mb-3" v-for="(order, index) in order_bys" :key="index">
                                <accordion-multi-select 
                                    :options="order_by_options" 
                                    :show_hidden_fields="true"  
                                    :field_name="'order_by_' + index" 
                                    v-model="order.value" 
                                    @update:modelValue="changeSelection('order_by')"
                                    :max_num_values="1">
                                </accordion-multi-select>
                                <div class="button-wrapper">
                                    <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                        {{ order.direction }}
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                        <li><a class="dropdown-item" href="#" @click.prevent="order.direction = 'ASC'">Ascending</a></li>
                                        <li><a class="dropdown-item" href="#" @click.prevent="order.direction = 'DESC'">Descending</a></li>
                                    </ul>
                                </div>
                            </div>
                            <div v-if="order_bys && order_bys.length > 0">
                                <template v-for="(order, index) in order_bys" :key="index">
                                    <a v-if="order.value && order.value.length > 0 && order.value[0] && !isColumnType(order.value[0])"
                                       href="#" 
                                       class="me-2"
                                       style="font-size: 0.75rem;"
                                       data-bs-toggle="modal" 
                                       data-bs-target="#add-metric-filter-modal"
                                       @click.prevent="editMetricFilter(order.value[0], getOrderByType(order.value[0]))">
                                        {{ order.value[0].admin_name || order.value[0].text }} <i class="fas fa-external-link-alt"></i>
                                    </a>
                                </template>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Max Results (<em>Optional</em>)</label>
                            <input type="number" class="form-control" v-model="local_chart.max_results" aria-label="Max Results">
                        </div>
                        <div class="mb-3" v-if="local_chart.type !== 'scorecard' && local_chart.type !== 'multi_metric_scorecard' && local_chart.type !== 'pie'">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" id="highlight-last-value" v-model="local_chart.highlight_last_value" aria-label="Highlight Last Value">
                                <label class="form-check-label" for="highlight-last-value">
                                    Highlight Only the Last Value 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                                        title="When checked, only the last column will be highlighted to visually emphasize the most recent value." alt="">
                                    </img>
                                </label>
                            </div>
                        </div>
                        <div class="mb-3" v-if="local_chart.type !== 'scorecard' && local_chart.type !== 'multi_metric_scorecard' && local_chart.type !== 'pie'">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" id="show-all-data-labels" v-model="local_chart.show_all_data_labels" aria-label="Show All Data Labels">
                                <label class="form-check-label" for="show-all-data-labels">
                                    Show All Data Labels 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                                        title="When checked, all data labels will be shown." alt="">
                                    </img>
                                </label>
                            </div>
                        </div>
                        <div class="mb-3" v-if="local_chart.type === 'column' || local_chart.type === 'horizontal_bar'">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" id="show-series-percentage" v-model="local_chart.show_series_percentage" aria-label="Show Series Percentage">
                                <label class="form-check-label" for="show-series-percentage">
                                    Show as Percentage of Series Total 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                                        title="When checked, the values will be shown as a percentage of the series total." alt="">
                                    </img>
                                </label>
                            </div>
                        </div>
                        <div class="mb-3" v-if="local_chart.type !== 'scorecard' && local_chart.type !== 'multi_metric_scorecard' && local_chart.type !== 'pie'">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" id="include-projections" v-model="local_chart.include_projections" aria-label="Include Projections">
                                <label class="form-check-label" for="include-projections">
                                    Include Projections 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                                        title="When checked, Avid will project out future years' results based upon the trends of recent prior years." alt="">
                                    </img>
                                </label>
                            </div>
                        </div>
                    </div>    
                </div>
                <div>                         
                    <div class="mb-3" v-if="local_chart.type !== 'information_card'">
                        <label class="form-label">
                            Preview Filters 
                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                                title="These are filters only applied to the chart while using the Chart Builder. They will be applied via the Report Page once 'in the wild.'" alt="">
                            </img>
                        </label>
                        <div>
                            <div class="badge badge-primary" v-for="(filter, index) in local_chart.temp_filters" :key="filter.pos">
                                {{ filter.text }}
                                <button class="btn btn-xs btn-link p-0 ps-1" title="Remove Filter"
                                    @click.prevent="removeTempFilter(index)">
                                    <i class="fas fa-close"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="row" v-if="local_chart.type === 'scorecard' || local_chart.type === 'multi_metric_scorecard'">
                        <div class="col-12">
                            <div class="mb-3">
                                <label class="form-label">
                                    Icon URL (Optional) 
                                    <a href="https://docs.google.com/spreadsheets/d/1j3jx0hLzTdl3JD1e-86a9mQtfQ9TYUZl0mFI-qrb9pQ/edit?gid=0#gid=0" 
                                    target="_blank">See the icon library here</a>
                                </label>

                                <input type="text" class="form-control" v-model="local_chart.icon" placeholder="Enter icon URL" aria-label="Enter icon URL">
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Background Color</label>
                                <select class="form-select" v-model="local_chart.class_name" aria-label="Background Color">
                                    <option value="white">White</option>
                                    <option value="passive_color">Passive Color</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="row" v-if="local_chart.type === 'information_card'">
                        <div class="col-8"> 
                            <div class="mb-1">
                                <label class="form-label">Icon or Image URL (Optional)</label>
                                <input type="text" class="form-control" v-model="textbox_details.icon_url" aria-label="Icon or Image URL (Optional)">
                            </div>
                            <div class="mb-1">
                                <label class="form-label">Icon Background Color (Optional)</label>
                                <select class="form-select" v-model="textbox_details.icon_background_color" aria-label="Icon Background Color (Optional)">
                                    <option value="" disabled selected>Select the background color...</option>
                                    <option value="red">Red</option>
                                    <option value="orange">Orange</option>
                                    <option value="yellow">Yellow</option>
                                    <option value="green">Green</option>
                                    <option value="blue">Blue</option>
                                    <option value="purple">Purple</option>
                                    <option value="white">White</option>
                                </select>
                            </div>
                            <div class="mb-1" v-if="local_chart.type === 'information_card'">
                                <label class="form-label">Card Background Color (Optional)</label>
                                <select class="form-select" v-model="local_chart.if_card_white" aria-label="Card Background Color (Optional)">
                                    <option :value="null" disabled>Select card background...</option>
                                    <option :value="false">Passive Color</option>
                                    <option :value="true">White</option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Content</label>
                                <QuillyEditor 
                                    ref="textboxEditor"
                                    v-model="textbox_details.html_content" 
                                    :options="editorOptions"
                                    @update:modelValue="textboxContentChange"
                                ></QuillyEditor>
                            </div>
                        </div>
                        <div class="col-4">
                            <p>Instruction of creating information_card:</p>
                            <ul>
                                <li>When includes an icon or image url, it will always appear on the left side (similar to scorecard)</li>
                                <li>3 types of heading allowed: Heading 1(24px), Heading 2(22px), Heading 3(20px), Normal(14px)</li>
                                <li>Image style: Max height 100%, max width 100%</li>
                                <li>
                                    <a href="https://docs.google.com/spreadsheets/d/1j3jx0hLzTdl3JD1e-86a9mQtfQ9TYUZl0mFI-qrb9pQ/edit?gid=0#gid=0" 
                                        target="_blank">See the icon library here</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    
                    <button id="preview-button" class="btn btn-secondary" 
                        @click="previewChart" 
                        :disabled="form.busy">
                        <i class="fa fa-eye"></i>Preview
                    </button>
                </div>
            </div>
        </div>
        <div class="modal fade" id="add-metric-filter-modal" data-bs-backdrop="static"
            aria-labelledby="add-metric-filter-modal" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="text-center w-100">
                            <button type="button" class="btn btn-white float-end close-modal"
                                data-bs-dismiss="add-metric-filter-modal" @click="closeModal" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                            <h3 class="mt-2">{{ propercase(add_new_option) }} Builder</h3>
                        </div>
                    </div>
                    <div class="modal-body pt-0 pb-0">
                        <metric-filter-builder 
                            :data_blend="local_chart.data_blend" 
                            :data_blends_array="data_blends_array" 
                            :modal_id="'add-metric-filter-modal'"
                            ref="mf_builder" 
                            :type="add_new_option" 
                            :clients="clients"
                            @update="mfAdded">
                        </metric-filter-builder>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { QuillyEditor } from 'vue-quilly';
import Quill from 'quill';

export default defineComponent({
    name: 'ChartConfiguration',
    components: {
        QuillyEditor
    },
    props: {
        clients: {
            type: Array,
            default: () => []
        },
        chart: {
            type: Object,
            required: true
        },
        render_large_chart: {
            type: Boolean,
            required: true
        },
        metrics_filters: {
            type: Array,
            required: true
        },
        data_blends_array: {
            type: Array,
            required: true
        },
        validate_data_blend: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            local_chart: {
                type: '',
                group_by: [],
                metrics: [],
                filter: [],
                order_by: [],
                temp_filters: [],
                data_blend: null,
                data_blend_type: '',
                include_projections: false,
                max_results: null,
                highlight_last_value: false,
                show_all_data_labels: false,
                show_series_percentage: false,
                icon: null,
                class_name: 'white',
                icon_background_color: '',
                if_card_white: null
            },
            columns: [], // pulled from database
            local_metrics: [], // pulled from database
            local_filters: [], // pulled from database
            local_dimensions: [], // pulled from database
            dimension_options: [],
            metric_options: [],
            filter_options: [],
            order_by_options: [],
            order_bys: [],
            max_values: {
                dimensions: 2,
                metrics: 2,
                order_by: 2
            },
            local_errors: {
                chart_type: '',
                group_by: '',
                metrics: ''
            },
            form: {
                successful: false,
                busy: false,
                error: false
            },
            add_new_option: '',
            textbox_details: {
                icon_url: '',
                icon_background_color: '',
                html_content: ''
            },
            editorOptions: {
                theme: 'snow',
                modules: {
                    toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['image', 'link']
                    ]
                },
                readOnly: false
            }
        };
    },
    beforeMount() {
        // Initialize local_chart with proper defaults
        this.local_chart = {
            ...this.local_chart,
            ...JSON.parse(JSON.stringify(this.chart))
        };
        
        this.local_metrics_filter = this.metrics_filters;
        this.loadMetricFilterColumnOptions();
        this.refreshColumns();
        
        if (this.local_chart.type !== 'information_card') {
            this.updateMFForDisplay();
        }
        
        // Always add the temporary filters
        if (this.chart.data_blend_type === 'constituent') {
            this.local_chart.temp_filters = this.getConstituentFilters();
        } else {
            this.local_chart.temp_filters = [];
        }
        
        this.local_chart.include_projections = false;

        // If the order by array is blank, set it to the default structure
        if (!this.local_chart.order_by) {
            this.addOrderBy();
        } else {
            // Load the order by array into the right structure for the UI
            this.order_bys = [];
            for (let i = 0; i < this.local_chart.order_by.length; i++) {
                this.order_bys.push({
                    value: [this.local_chart.order_by[i]], 
                    direction: this.local_chart.order_by[i].direction
                });
            }
            if (this.order_bys.length === 0) {
                this.addOrderBy();
            }
        }

        // Load information_card configuration if this is a information_card chart
        if (this.local_chart.type === 'information_card') {
            this.loadTextboxConfig();
            if (this.chart.icon !== undefined) {
                this.local_chart.icon = this.chart.icon;
            }
            if (this.chart.icon_background_color !== undefined) {
                this.local_chart.icon_background_color = this.chart.icon_background_color;
            }
            if (this.chart.if_card_white !== undefined) {
                this.local_chart.if_card_white = this.chart.if_card_white;
            }
        }

        // Initialize scorecard fields if they exist in the chart prop
        if (this.local_chart.type === 'scorecard' || this.local_chart.type === 'multi_metric_scorecard') {
            if (this.chart.icon !== undefined) {
                this.local_chart.icon = this.chart.icon;
            }
            if (this.chart.class_name !== undefined) {
                this.local_chart.class_name = this.chart.class_name;
            } else {
                this.local_chart.class_name = 'white';
            }
        }

        // Ensure max value limits are correctly applied when editing an existing chart
        // by initializing chart-type specific limits (e.g., tables should allow up to 10 metrics).
        this.changeChartTypeSelection();
    },
    computed: {
        max_metrics () {
            if(this.local_chart.group_by != null && this.local_chart.group_by.length === 2
                && this.local_chart.type === 'column'){
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 1)
                    this.local_chart.metrics = [this.local_chart.metrics[0]];
                return 1;
            }
            return this.max_values.metrics;
        },
    },
    watch:{
        'chart.data_blend': {
            handler(n, o) {
                this.local_chart.data_blend = JSON.parse(JSON.stringify(this.chart.data_blend));
                this.local_chart.data_blend_type = this.chart.data_blend_type;
                if(this.local_chart.data_blend_type === 'constituent')
                    this.local_chart.temp_filters = this.getConstituentFilters();
                else
                    this.local_chart.temp_filters = [];
                this.loadMetricFilterColumnOptions();
                this.refreshColumns();
            },
            deep: true
        }
    },
    methods: {
        shouldBypassChartCache() {
            try {
                const params = new URLSearchParams(window.location.search);
                return params.get('bypass_chart_cache') === '1' || params.get('bypass_chart_cache') === 'true';
            } catch (_) {
                return false;
            }
        },
        updateMFForDisplay() {
            if(this.local_chart.group_by && this.local_chart.group_by.length > 0)
                //Loop through group_by and add the display name and admin name values using the matching value in the local_dimensions
                for(var i = 0; i < this.local_chart.group_by.length; i++)
                {
                    for(var j = 0; j < this.local_dimensions.length; j++)
                    {
                        if(this.local_chart.group_by[i].value == this.local_dimensions[j].value)
                        {
                            this.local_chart.group_by[i].display_name = this.local_dimensions[j].display_name;
                            this.local_chart.group_by[i].admin_name = this.local_dimensions[j].admin_name;
                            break;
                        }
                    }
                }
            if(this.local_chart.metrics && this.local_chart.metrics.length > 0)
                //Do the same thing for metrics
                for(var i = 0; i < this.local_chart.metrics.length; i++)
                {
                    for(var j = 0; j < this.local_metrics.length; j++)
                    {
                        if(this.local_chart.metrics[i].value == this.local_metrics[j].value)
                        {
                            this.local_chart.metrics[i].display_name = this.local_metrics[j].display_name;
                            this.local_chart.metrics[i].admin_name = this.local_metrics[j].admin_name;
                            break;
                        }
                    }
                }
        },  
        getConstituentFilters() {
            return [
                {
                    'pos': 0,
                    'main_category': 'Temp Filters',
                    'category': '',
                    'text': 'Last 5 Years',
                    'value': 'temp_filter_1',
                    'formula': 'transactions.time_period._periods_back <= 5'
                },
                {
                    'pos': 1,
                    'main_category': 'Temp Filters',
                    'category': '',
                    'text': 'Time Period: Closed Fiscal Year',
                    'value': 'temp_filter_2',
                    'formula': "transactions.time_period.year_type = 'Closed Fiscal Year'"
                }
            ];
        },
        changeSelection(field_name) {
            this.local_chart[field_name].forEach(item => {
                if (!item.format || !item.color) {
                    var selected_option = this.order_by_options.find(option => option.value === item.value);  // this.order_by_options has the completed list
                    item.format = selected_option ? selected_option.format : 'number';
                    item.color = selected_option ? selected_option.color : 'blue';
                }
            });
        },
        addOrderBy() {
            this.order_bys.push({value: null, direction: 'DESC'});
        },
        removeTempFilter(index) {
            this.local_chart.temp_filters.splice(index, 1);
            this.$forceUpdate();
        },
        textboxContentChange(){
            this.generateTextboxContent();
        },
        loadTextboxConfig(){
            if (this.local_chart.icon){
                this.textbox_details.icon_url = this.local_chart.icon;
            }
            if (this.local_chart.icon_background_color){
                this.textbox_details.icon_background_color = this.local_chart.icon_background_color;
            }
            if (this.local_chart.html){
                this.textbox_details.html_content = decodeURIComponent(this.local_chart.html);
            }
        },
        refreshColumns(){
            if (!this.local_chart.data_blend) {
                return;
            }
            var data = {
              include_trans: true,
              source: 'data_blend',
              data_source_id: this.local_chart.data_blend.id
            };
            this.columns = [];

            window.axios.post('/api/bigquery/get_db_columns', data)
              .then(response => {
                var cols = response.data.columns;
                var headers = "";
                for(var i = 0; i < cols.length; i++){

                    cols[i].main_category = 'Columns';
                }
                this.columns = cols;
                this.loadMultiSelectFieldsOptions();
              });
        },
        loadMetricFilterColumnOptions(){
            var metrics = [];
            var filters = [];
            var dimensions = [];

            this.local_metrics_filter.forEach(metric_filter_obj =>{
                //If it is a specific client and that client isn't selected
                if(metric_filter_obj.data_blend_id != null && metric_filter_obj.data_blend_id != this.local_chart.data_blend_id)
                    return;

                if (metric_filter_obj.type == 'metric' && metric_filter_obj.data_blend_type == this.local_chart.data_blend_type){
                    metric_filter_obj.main_category = 'Metrics';
                    metric_filter_obj.text = metric_filter_obj.admin_name;
                    metric_filter_obj.admin_name = metric_filter_obj.admin_name;
                    metric_filter_obj.display_name = metric_filter_obj.name;
                    metric_filter_obj.value = metric_filter_obj.id;
                    metrics.push(metric_filter_obj)
                } else if (metric_filter_obj.type == 'filter' && metric_filter_obj.data_blend_type == this.local_chart.data_blend_type){
                    metric_filter_obj.main_category = 'Filters';
                    metric_filter_obj.text = metric_filter_obj.admin_name;
                    metric_filter_obj.admin_name = metric_filter_obj.admin_name;
                    metric_filter_obj.display_name = metric_filter_obj.name;
                    metric_filter_obj.value = metric_filter_obj.id;
                    filters.push(metric_filter_obj)
                }
                else if (metric_filter_obj.type == 'dimension' && metric_filter_obj.data_blend_type == this.local_chart.data_blend_type){
                    metric_filter_obj.main_category = 'Dimensions';
                    metric_filter_obj.text = metric_filter_obj.admin_name;
                    metric_filter_obj.admin_name = metric_filter_obj.admin_name;
                    metric_filter_obj.display_name = metric_filter_obj.name;
                    metric_filter_obj.value = metric_filter_obj.id;
                    dimensions.push(metric_filter_obj)
                }
            }) 
            this.local_metrics = metrics;
            this.local_filters = filters;
            this.local_dimensions = dimensions;

            this.loadMultiSelectFieldsOptions()
        },
        loadMultiSelectFieldsOptions(){
                this.dimension_options = this.loadOptionsByCategory(['columns','dimensions'])
                this.metric_options = this.loadOptionsByCategory(['metrics'])     
                this.filter_options = this.loadOptionsByCategory(['filters'])   
                this.order_by_options = this.loadOptionsByCategory(['metrics','columns','dimensions'])
        },
        loadOptionsByCategory(categoryArray){
            var options = []
            
            //Loop through the array (this way the dropdown will be in the order passed into the array)
            for(let i = 0; i < categoryArray.length; i++){
                if (categoryArray[i] == 'metrics'){
                    options.push(...this.local_metrics)
                }
                if (categoryArray[i] =='filters'){
                    options.push(...this.local_filters)
                }
                if (categoryArray[i] == 'columns'){
                    options.push(...this.columns)
                }
                if (categoryArray[i] == 'dimensions'){
                    options.push(...this.local_dimensions)
                }
            }
            return options
        },
        changeChartTypeSelection(){
            this.clearErrorForField('chart_type');

            // Column
            if(this.local_chart.type == 'column' || this.local_chart.type == 'horizontal_bar'){
                //If there are two dimensions, there can only be one metric but that is handled in a different spot

                //Max two dimenions
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 2)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 2);
                this.max_values.dimensions=2;
                //Max two metrics
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 2)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 2);
                this.max_values.metrics=2;
                if(this.order_bys != null && this.order_bys.length > 2)
                    this.order_bys = this.order_bys.slice(0, 2);
                this.max_values.order_by=2;
            }
            // Stack column and bar
            else if(this.local_chart.type == 'stacked_column' || this.local_chart.type == '100_percent_stacked_column' 
            || this.local_chart.type == 'stacked_bar' || this.local_chart.type == '100_percent_stacked_bar'){
                //Max two dimenions
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 2)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 2);
                this.max_values.dimensions=2;
                //Max two metrics
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 2)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 2);
                this.max_values.metrics=2;
                if(this.order_bys != null && this.order_bys.length > 2)
                    this.order_bys = this.order_bys.slice(0, 2);
                this.max_values.order_by=2;
            }
            // line chart
            else if(this.local_chart.type == 'line' || this.local_chart.type == 'cumulative_line'){
                //Max two dimenions
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 2)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 2);
                this.max_values.dimensions=2;
                //Max 5 metrics for now
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 5)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 5);
                this.max_values.metrics=5;
                if(this.order_bys != null && this.order_bys.length > 5)
                    this.order_bys = this.order_bys.slice(0, 5);
                this.max_values.order_by=5;
            }
            // bar-line combo chart
            else if(this.local_chart.type == 'bar_line'){
                //Only one dimension
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 1)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 1);
                this.max_values.dimensions=1;
                
                //Max 5 metrics for now
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 5)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 5);
                this.max_values.metrics=5;

                if(this.order_bys != null && this.order_bys.length > 5)
                    this.order_bys = this.order_bys.slice(0, 5);
                this.max_values.order_by=5;
            }
            // Pie Chart and scorecard
            else if(this.local_chart.type == 'pie' || this.local_chart.type == 'scorecard' || this.local_chart.type == 'multi_metric_scorecard'){
                //Only one dimension
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 1)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 1);
                this.max_values.dimensions=1;

                //Only one metric (except multi-metric scorecard)
                if (this.local_chart.type === 'multi_metric_scorecard') {
                    if(this.local_chart.metrics != null && this.local_chart.metrics.length > 4)
                        this.local_chart.metrics = this.local_chart.metrics.slice(0, 4);
                    this.max_values.metrics=4;
                } else {
                    if(this.local_chart.metrics != null && this.local_chart.metrics.length > 1)
                        this.local_chart.metrics = this.local_chart.metrics.slice(0, 1);
                    this.max_values.metrics=1;
                }

                //Only one ordering
                if(this.order_bys != null && this.order_bys.length > 1)
                    this.order_bys = this.order_bys.slice(0, 1);
                this.max_values.order_by=1;

                // Initialize scorecard-specific fields
                if(this.local_chart.type == 'scorecard') {
                    if(this.local_chart.icon === undefined) {
                        this.local_chart.icon = null;
                    }
                    if(this.local_chart.class_name === undefined) {
                        this.local_chart.class_name = 'white';
                    }
                }

            // Table
            } else if(this.local_chart.type == 'table'){
                // allow at most 6 dimensions, "unlimited" metrics and order_by
                // Will have another table template allows multiple dimensions
                if(this.local_chart.group_by != null && this.local_chart.group_by.length > 6)
                    this.local_chart.group_by = this.local_chart.group_by.slice(0, 6);
                this.max_values.dimensions=6;

                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 10)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 10);
                this.max_values.metrics=10;

                if(this.order_bys != null && this.order_bys.length > 10)
                    this.order_bys = this.order_bys.slice(0, 10);
                this.max_values.order_by=10;
            } 

            // Multi-Dimension Table
            else if(this.local_chart.type == 'multi_dimension_table'){
                if(this.local_chart.metrics != null && this.local_chart.metrics.length > 10)
                    this.local_chart.metrics = this.local_chart.metrics.slice(0, 10);
                this.max_values.metrics=10;

                if(this.order_bys != null && this.order_bys.length > 10)
                    this.order_bys = this.order_bys.slice(0, 10);
                this.max_values.order_by=10;
            }
            // information_card
            else if(this.local_chart.type == 'information_card'){
                // Initialize Quill editors
                this.$nextTick(() => {
                    if (this.$refs.textboxEditor) {
                        this.$refs.textboxEditor.initialize(Quill);
                        this.initializeQuillTooltips();
                    }
                });
            }

            this.$forceUpdate();
            this.$emit('updateChartConfig', this.local_chart);
        },
        mfAdded(mf){
            // Check if this metric/filter already exists (update vs new)
            const existingIndex = this.local_metrics_filter.findIndex((item) => item.id === mf.id);
            if (existingIndex >= 0) {
                // Update existing
                this.local_metrics_filter[existingIndex] = mf;
                
                // Update selected values in chart if they exist
                if (this.local_chart.group_by) {
                    this.local_chart.group_by.forEach((item) => {
                        if (item.value === mf.id) {
                            item.admin_name = mf.admin_name;
                            item.display_name = mf.name;
                            item.text = mf.admin_name;
                        }
                    });
                }
                if (this.local_chart.metrics) {
                    this.local_chart.metrics.forEach((item) => {
                        if (item.value === mf.id) {
                            item.admin_name = mf.admin_name;
                            item.display_name = mf.name;
                            item.text = mf.admin_name;
                        }
                    });
                }
                if (this.local_chart.filter) {
                    this.local_chart.filter.forEach((item) => {
                        if (item.value === mf.id) {
                            item.admin_name = mf.admin_name;
                            item.display_name = mf.name;
                            item.text = mf.admin_name;
                        }
                    });
                }
            } else {
                // Add new
                this.local_metrics_filter.push(mf);
            }
            this.loadMetricFilterColumnOptions()
            this.closeModal();
        },
        isColumnType(item) {
            // Check if item is a column (not a metric/filter/dimension)
            // Columns have main_category "Columns" or type that's a data type (string, number, etc.)
            if (!item) return false;
            
            // Check main_category first
            if (item.main_category === 'Columns') {
                return true;
            }
            
            // Check if type is a data type rather than metric/filter/dimension
            const dataTypes = ['string', 'number', 'integer', 'float', 'double', 'boolean', 'date', 'datetime', 'timestamp'];
            if (item.type && dataTypes.includes(item.type.toLowerCase())) {
                return true;
            }
            
            // If it's a column, the value will be a string path like "activities.name"
            if (typeof item.value === 'string' && item.value.includes('.')) {
                // Check if it's not in our metrics/filters/dimensions lists
                const isInLists = (this.local_metrics && this.local_metrics.find(m => m.id === item.value)) ||
                                 (this.local_filters && this.local_filters.find(f => f.id === item.value)) ||
                                 (this.local_dimensions && this.local_dimensions.find(d => d.id === item.value));
                if (!isInLists) {
                    return true;
                }
            }
            
            return false;
        },
        editMetricFilter(item, type) {
            // Don't try to edit columns
            if (this.isColumnType(item)) {
                return;
            }
            
            // Find the full metric/filter/dimension object from local_metrics_filter
            const fullObject = this.local_metrics_filter.find((mf) => mf.id === item.value);
            if (fullObject) {
                this.add_new_option = type;
                this.$refs.mf_builder.setMetricFilter(fullObject);
            }
        },
        getOrderByType(item) {
            // Safety check
            if (!item || !item.value) {
                return 'Metric';
            }
            // Determine the type based on which array contains the item
            if (this.local_metrics && this.local_metrics.find((m) => m.id === item.value)) {
                return 'Metric';
            } else if (this.local_dimensions && this.local_dimensions.find((d) => d.id === item.value)) {
                return 'Dimension';
            } else if (this.local_filters && this.local_filters.find((f) => f.id === item.value)) {
                return 'Filter';
            }
            // Default to Metric if not found
            return 'Metric';
        },
        clearErrorForField(field_name){
            this.local_errors[field_name] = '';
        },
        clearErrors() {//reset errors to empty strings
            this.local_errors = {
                chart_type:'',
                group_by:'',
                metrics:''
            }
        },
        hasErrors() {//validate the form
            if(this.local_chart.type == null || this.local_chart.type == ""){
                this.local_errors.chart_type = "The chart type is required.";
            } else
                this.local_errors.chart_type = "";
            
            const scorecardTypesAllowNoDimension = ['scorecard', 'multi_metric_scorecard'];
            if(!scorecardTypesAllowNoDimension.includes(this.local_chart.type) && (this.local_chart.group_by == null || this.local_chart.group_by == ""
                || this.local_chart.group_by.length==0) ){
                this.local_errors.group_by = "A dimension is required.";
            } else
                this.local_errors.group_by = "";

            if(this.local_chart.metrics == null || this.local_chart.metrics == ""
                || this.local_chart.metrics.length==0){
                this.local_errors.metrics = "A metric is required.";
            } else
                this.local_errors.metrics = "";

            if (this.local_errors.chart_type!="" || this.local_errors.group_by!="" || this.local_errors.metrics!="")
                return true

            return false
        },
        getOrderBys() {
            let ob = [];
            for(var i = 0; i < this.order_bys.length; i++)
            {
                if(this.order_bys[i].value != null && this.order_bys[i].value != "") {
                    this.order_bys[i].value[0].direction = this.order_bys[i].direction;
                    ob.push(this.order_bys[i].value[0]);
                }
                else {
                    this.order_bys.splice(i, 1);
                    i--;
                }
            }
            if(this.order_bys.length == 0)
                this.addOrderBy();

            return ob;
        },
        getChart() {
            if (this.local_chart.type === 'information_card') {
                this.generateTextboxContent();
            }
            return this.local_chart;
        },
        generateTextboxContent(){
            this.local_chart.icon = this.textbox_details.icon_url;
            this.local_chart.icon_background_color = this.textbox_details.icon_background_color;
            this.local_chart.html = encodeURIComponent(this.textbox_details.html_content);
            this.$emit('updateChartData', this.local_chart);
        },
        previewChart(){
            this.clearErrors();

            if (this.local_chart.type !== 'information_card') {
                if(this.hasErrors()) {
                    return;
                }
                this.form.busy = true;

                this.local_chart.order_by = this.getOrderBys();
                //Loop through order_by, metrics, and group_by and change the text to the display_name value 
                
                this.updateText(this.local_chart.group_by);
                this.updateText(this.local_chart.metrics);

                if(this.validate_data_blend){
                    this.local_chart.data_blend = this.validate_data_blend;
                }
                this.$emit('loadingChartData', true);

                let requestPayload = this.local_chart;
                if(this.local_chart.type == 'multi_dimension_table'){
                    // Send only the first dimension for initial load; table dropdown can switch dimensions without page refresh
                    var firstGroupBy = (this.local_chart.group_by && this.local_chart.group_by.length > 0)
                        ? [{ ...this.local_chart.group_by[0] }]
                        : [];
                    requestPayload = { ...this.local_chart, group_by: firstGroupBy };
                }
                if (this.shouldBypassChartCache()) {
                    requestPayload = { ...requestPayload, bypass_cache: true };
                }
                window.axios.post('/api/chart/get_data', requestPayload)
                    .then(response => {
                        this.form.busy = false;
                        this.local_chart.data = response.data.data;
                        this.local_chart.labels = response.data.labels;
                        this.local_chart.metric_formats = response.data.formats;
                        this.$emit('updateChartData', this.local_chart);
                });   
                //Now change them back
                if(this.local_chart.metrics && this.local_chart.metrics.length > 0)
                    for(var i = 0; i < this.local_chart.metrics.length; i++)
                        if(this.local_chart.metrics[i].admin_name)
                            this.local_chart.metrics[i].text = this.local_chart.metrics[i].admin_name;
                
                if(this.local_chart.group_by && this.local_chart.group_by.length > 0)
                    for(var i = 0; i < this.local_chart.group_by.length; i++){
                        if(this.local_chart.group_by[i].admin_name)
                            this.local_chart.group_by[i].text = this.local_chart.group_by[i].admin_name;
                    }

            } else if (this.local_chart.type === 'information_card') {
                this.generateTextboxContent();
                this.form.busy = false;
            }
        },

        updateText(arr){
            if(arr && arr.length > 0)
                for(var i = 0; i < arr.length; i++){
                    if (arr[i].display_name)
                        arr[i].text = arr[i].display_name;
                    //If value exists and is a string
                    else if(arr[i].value && typeof arr[i].value === 'string') {
                        //Split the value by . and then, take the last value and call the propercase filter
                        let split = arr[i].value.split('.');
                        //Replace _ with a space and capitalize the first letter of each word
                        let value = String(split[split.length-1]).replaceAll("_", " ");

                        value=  value.replace(
                            /\w\S*/g,
                            function(txt) {
                                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                            });
                        arr[i].text = value;
                    }
                }
        },
        closeModal() {
            var myModalEl = document.getElementById('add-metric-filter-modal')
            var modal = bootstrap.Modal.getInstance(myModalEl);
            modal.hide();
        },
        initializeInformationCardEditor() {
            if (this.$refs.informationCardEditor) {
                this.$refs.informationCardEditor.initialize(Quill);
                this.initializeQuillTooltips();
            }
        },
        initializeQuillTooltips() {
            const toolbarButtons = [
                { selector: '.ql-header[value="1"]', title: 'Heading 1' },
                { selector: '.ql-header[value="2"]', title: 'Heading 2' },
                { selector: '.ql-header[value="3"]', title: 'Heading 3' },
                { selector: '.ql-bold', title: 'Bold' },
                { selector: '.ql-italic', title: 'Italic' },
                { selector: '.ql-underline', title: 'Underline' },
                { selector: '.ql-list[value="ordered"]', title: 'Numbered List' },
                { selector: '.ql-list[value="bullet"]', title: 'Bulleted List' },
                { selector: '.ql-image', title: 'Insert Image' },
                { selector: '.ql-link', title: 'Insert Link' }
            ];

            toolbarButtons.forEach(({ selector, title }) => {
                const buttons = document.querySelectorAll(selector);
                buttons.forEach(button => {
                    button.setAttribute('data-bs-toggle', 'tooltip');
                    button.setAttribute('data-bs-placement', 'bottom');
                    button.setAttribute('title', title);
                });
            });

            // Initialize tooltips
            const tooltipTriggerList = document.querySelectorAll('.ql-toolbar [data-bs-toggle="tooltip"]');
            [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
        }
    }
});
</script>
